import type { ApiDashboardElement, SourceMap } from '@highimpact-frontend/api-types';
import type { createInitialPlotPreferences } from '@highimpact/scatterplot';

import { resolveElementMeasureColumnNames } from './buildElementPlotMetadata';
import type { ElementMeasurePlotType } from './elementMeasureSupport';

type CurveInitSettings = NonNullable<
  Parameters<typeof createInitialPlotPreferences>[0]
>[number];

type AxisSlot = {
  readonly axisName?: string;
  readonly axis_type?: string;
};

/** Builds scatterplot curve init settings from element JSON and resolved labels. */
export function buildScatterCurveInitFromElement(
  element: ApiDashboardElement,
  sourceLabelMap: SourceMap,
  plotType: Extract<ElementMeasurePlotType, 'timeseries' | 'scatter'>
): CurveInitSettings[] {
  const properties = element.properties as {
    additional_properties?: { curveToSourceIndex?: Record<string, number> };
    data_source_to_axis?: Record<number, AxisSlot>;
  };

  const columnNames = resolveElementMeasureColumnNames(element, sourceLabelMap);
  const curveToSourceIndex =
    properties.additional_properties?.curveToSourceIndex ?? {};
  const axisByIndex = properties.data_source_to_axis ?? {};
  const isScatter = plotType === 'scatter';

  return columnNames.map((name, index) => {
    const curveId =
      Object.entries(curveToSourceIndex).find(
        ([, sourceIndex]) => sourceIndex === index
      )?.[0] ?? `curve_${index}`;

    const axisMeta = axisByIndex[index];

    return {
      curveId,
      axisKey: String(index),
      curveName: name,
      axisName: axisMeta?.axisName ?? name,
      thicknessPx: isScatter ? 10 : 2,
      isX: isScatter && index === columnNames.length - 1,
    };
  });
}
